import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Speed,
} from "@mui/icons-material";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Tables } from "../../../database.types";
import { StandardButton } from "../../components/StandardButton";
import { StandardTextInput } from "../../components/StandardTextInput";
import NumberInput from "../../components/NumberInput";
import {
  AutoSaveIndicator,
  AutoSaveStatus,
} from "../../components/AutoSaveIndicator";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";

export const numberInputDarkSx = {
  "& .MuiOutlinedInput-root": {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: 1,
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.6)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.8)",
        borderWidth: 2,
      },
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.03)",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.12)",
      },
    },
  },
  "& .MuiInputBase-input": {
    color: "white",
    fontWeight: 500,
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
      opacity: 1,
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-focused": { color: "white" },
  },
};

type ClosestGuessQuestionRow = Tables<"closest_guess_questions">;

interface ClosestGuessEditorProps {
  bonusRoundId: string;
  disabled: boolean;
}

const DEBOUNCE_MS = 600;

const makeEmptyQuestion = (
  bonusRoundId: string,
  order: number,
): ClosestGuessQuestionRow => ({
  id: uuidv4(),
  bonus_round_id: bonusRoundId,
  question_text: "",
  correct_answer: null,
  question_order: order,
  team_1_answer: null,
  team_2_answer: null,
  created_at: null,
  updated_at: null,
});

export const ClosestGuessEditor = ({
  bonusRoundId,
  disabled,
}: ClosestGuessEditorProps) => {
  const theme = useTheme();
  const { supabase } = useSupabase();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<ClosestGuessQuestionRow[]>([]);

  // Auto-save indicator
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markSaving = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setAutoSaveState("saving");
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveState("saved");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 2000);
  }, []);

  const markError = useCallback(() => {
    setAutoSaveState("error");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 3000);
  }, []);

  // ── Fetch on mount ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const fetched = await gameService.getClosestGuessQuestions(
          supabase,
          bonusRoundId,
        );
        if (cancelled) return;

        if (fetched.length === 0) {
          setQuestions([makeEmptyQuestion(bonusRoundId, 0)]);
        } else {
          setQuestions(fetched);
        }
      } catch (err) {
        console.error("Error loading closest guess questions:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [supabase, bonusRoundId]);

  // ── Debounced save ──────────────────────────────────────────────────
  const debouncedSave = useMemo(
    () =>
      debounce(async (currentQuestions: ClosestGuessQuestionRow[]) => {
        // Validate: all non-null answers must be numbers
        const hasInvalidAnswer = currentQuestions.some(
          (q) => q.correct_answer !== null && isNaN(q.correct_answer),
        );
        if (hasInvalidAnswer) return;

        markSaving();
        try {
          await gameService.replaceClosestGuessQuestions(
            supabase,
            bonusRoundId,
            currentQuestions.map((q) => ({
              question_text: q.question_text,
              correct_answer: q.correct_answer,
              question_order: q.question_order,
            })),
          );
          markSaved();
        } catch {
          markError();
        }
      }, DEBOUNCE_MS),
    [supabase, bonusRoundId, markSaving, markSaved, markError],
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      debouncedSave.flush();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, [debouncedSave]);

  // ── Handlers ────────────────────────────────────────────────────────

  const handleQuestionTextChange = (idx: number, value: string) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], question_text: value };
      debouncedSave(updated);
      return updated;
    });
  };

  const handleAnswerChange = (idx: number, value: number | null) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], correct_answer: value };
      debouncedSave(updated);
      return updated;
    });
  };

  const addQuestion = () => {
    setQuestions((prev) => {
      const updated = [...prev, makeEmptyQuestion(bonusRoundId, prev.length)];
      debouncedSave(updated);
      return updated;
    });
  };

  const removeQuestion = (idx: number) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated.splice(idx, 1);
      debouncedSave(updated);
      return updated;
    });
  };

  // ── Loading state ───────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <AutoSaveIndicator status={autoSaveState} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "rgba(255, 255, 255, 0.7)",
              fontWeight: 600,
              mb: 2,
            }}
          >
            <Speed fontSize="small" />
            Questions ({questions.length})
          </Typography>
        </Box>

        {questions.map((q, idx) => (
          <Box
            key={q.id}
            sx={{
              p: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              borderBottom:
                idx !== questions.length - 1
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
              bgcolor:
                idx % 2 === 0 ? "transparent" : "rgba(255, 255, 255, 0.02)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Chip
                label={idx + 1}
                size="small"
                color="secondary"
                sx={{ minWidth: 28, flexShrink: 0 }}
              />
              <StandardTextInput
                label="Question"
                value={q.question_text}
                onChange={(e) => handleQuestionTextChange(idx, e.target.value)}
                fullWidth
                disabled={disabled}
                size="small"
                placeholder="Enter the question"
              />
              <Tooltip title="Remove Question">
                <IconButton
                  color="error"
                  onClick={() => removeQuestion(idx)}
                  disabled={disabled}
                  size="small"
                  sx={{ flexShrink: 0 }}
                  tabIndex={-1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                pl: 4.5,
              }}
            >
              <NumberInput
                value={q.correct_answer}
                size="small"
                onEnter={() => {
                  addQuestion();
                  setTimeout(() => {
                    const nextInput = document.getElementById(
                      `closest-guess-answer-input-${idx + 1}`,
                    );
                    nextInput?.focus();
                    nextInput?.scrollIntoView({ behavior: "smooth" });
                  }, 50);
                }}
                onChange={(n) => handleAnswerChange(idx, n)}
                disabled={disabled}
                sx={{
                  width: "calc(100% - 36px)",
                  ...numberInputDarkSx,
                }}
                placeholder="Enter number"
              />
            </Box>
          </Box>
        ))}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <StandardButton
          startIcon={<AddIcon />}
          variant="outlined"
          color="secondary"
          disabled={disabled}
          onClick={addQuestion}
          size="small"
        >
          Add Question
        </StandardButton>
      </Box>
    </Box>
  );
};
